import { Box } from "@mui/material";
import { ReactNode, RefObject, useEffect, useState } from "react";

type HorizontalScrollerProps = {
  children: ReactNode;
  overlayElement?: ReactNode;
  scrollRef: RefObject<HTMLDivElement>;
  itemWidth: number;
  numberOfItems?: number;
  containerOffset?: number;
};

export default function HorizontalScroller({
  children,
  overlayElement,
  scrollRef,
  itemWidth = 100,
  numberOfItems = 1,
  containerOffset = 0,
}: HorizontalScrollerProps) {
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      if (scrollRef.current?.parentElement?.parentElement) {
        const parentWidth =
          scrollRef.current.parentElement.parentElement.clientWidth -
          containerOffset;

        // How many full items can fit
        const maxVisibleItems = Math.floor(parentWidth / itemWidth) || 1;
        const visibleItems = Math.min(maxVisibleItems, numberOfItems);

        const newWidth =
          visibleItems < 2
            ? scrollRef.current.parentElement.parentElement.clientWidth
            : visibleItems * itemWidth + containerOffset;

        // Set scroll container width to fit exactly that many items
        setContainerWidth(newWidth);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [scrollRef, itemWidth]);

  return (
    <Box width={`${containerWidth}px`} sx={{ position: "relative" }}>
      {overlayElement}
      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          overflowX: "auto",
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { display: "none" },
          overflowY: "visible",
        }}
        width={`${containerWidth}px`}
      >
        {children}
      </Box>
    </Box>
  );
}
